import { IHttpService, IPromise } from "angular";
import { addDays, compareAsc, compareDesc, isFuture, parseJSON } from "date-fns";
import { BlockConfigurationObject } from "../interfaces/block-configuration-object.interface";
import { BookOffer } from "../interfaces/book-offer.model";
import { FixtureRound } from "../interfaces/fixture-round.interface";
import { MatchLiveData } from "../interfaces/match-live-data.interface";
import { Match } from "../interfaces/match.interface";
import { TeamFuturesRecord } from "../interfaces/team-futures-record.interface";
import { TennisTournament } from "../interfaces/tennis-tournament.interface";
import { ConciseMatch } from "../interfaces/concise-match.interface";
import { MatchPropBet } from "../interfaces/match-prop-bet.interface";

export class SportDataService {

	_$http: IHttpService
	// _Rollbar: any;
	_apiDomain: string;

	/* @ngInject */
	constructor($http: IHttpService, /*Rollbar: any,*/ apiDomain: string, configurationObject: BlockConfigurationObject) {
		this._$http = $http;
		// this._Rollbar = Rollbar;
		if (configurationObject.apiDomain) {
			this._apiDomain = configurationObject.apiDomain;
		} else {
			this._apiDomain = apiDomain;
		}
	}

	getFuturesTeams(sport: string): IPromise<Array<TeamFuturesRecord>> {
		return this._$http.get(`${this._apiDomain}/futures?Sport=${sport}`).then((response) => {
			if (response && response.data) {
				return response.data as Array<TeamFuturesRecord>;
			} else {
				throw "Futures data in unexpected format: " + response;
			}
			
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	sport: sport,
				// 	error: err,
				// });
			}
			
			throw "API_CALL_FAILED";
		});
	}

	getUpcomingGames(sports: Array<string>, strip = false, bestBets = false, bookmakerList?: Array<string>, sportExclusiveBookmakers?: Record<string, string>): IPromise<Array<Match>> {
		return this._$http.post(`${this._apiDomain}/matches/upcoming?Sport=${sports.join(",")}${strip ? "&strip=true" : ""}${bestBets ? `&best_bets=true&bookmakers=${bookmakerList.join(",")}` : ""}`,{
			sport_exclusive_bookmakers: sportExclusiveBookmakers
		}).then((response) => {
			if (response.data || response.data === []) {
				return response.data as Array<Match>;
			} else {
				throw "Upcoming Matches data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getConciseUpcomingMatchData(sports: Array<string>): IPromise<Array<ConciseMatch>> {
		return this._$http.get<any>(`${this._apiDomain}/matches/upcoming/ids?dates=true&Sport=${sports.join(",")}&days=7`).then((response) => {
			if (response.data?.Matches || response.data.Matches === []) {
				return response.data.Matches as Array<ConciseMatch>;
			} else {
				throw "Upcoming Matches data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getPropsQuickPicksData(sports: Array<string>, bookmakerList?: Array<string>, sportExclusiveBookmakers?: Record<string, string>):
		IPromise<{picks: Array<MatchPropBet>, prop_categories: Record<string, Record<string, string>>}> {
		return this._$http.post<{picks: Array<MatchPropBet>, prop_categories: Record<string, Record<string, string>>}>(
			`${this._apiDomain}/props/quickpicks?sport=${sports.join(",")}${bookmakerList ? `&bookmakers=${bookmakerList.join(",")}`: ""}`,
			{
				sport_exclusive_bookmakers: sportExclusiveBookmakers
			}
		)
			.then(
				(response) => {
					if (response.data || response.data === []) {
						return response.data;
					} else {
						throw "Upcoming Matches data in unexpected format: " + response;
					}
				})
			.catch((err) => {
				console.error(err);
				if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
					// this._Rollbar.warning("Levy call failed", {
					// 	error: err,
					// });
				}
				throw "API_CALL_FAILED";
			})
		;
	}

	getPreMatchData(matchID: string, bestBets = false, bookmakerList?: Array<string>): IPromise<Match> {
		return this._$http.get(`${this._apiDomain}/pre/${matchID}${bestBets ? `?best_bets=true&bookmakers=${bookmakerList.join(",")}` : ""}`).then((response) => {
			if (response.data) {
				return response.data as Match;
			} else {
				throw "Pre-Match data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getLiveMatchData(matchID: string): IPromise<MatchLiveData> {
		return this._$http.get(`${this._apiDomain}/live/${matchID}`).then((response) => {
			if (response.data) {
				return response.data as MatchLiveData;
			} else {
				throw "Live Match data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (err.status === 404) {
				return {error: true};
			}
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getFullMatchData(matchID: string, bestBets = false, bookmakerList?: Array<string>): IPromise<Match> {

		return this._$http.get(`${this._apiDomain}/match/${matchID}${bestBets ? `?best_bets=true&bookmakers=${bookmakerList.join(",")}` : ""}`).then((response) => {
			if (response.data) {
				return response.data as Match;
			} else {
				throw "Match data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getRoundOptions(sportCode: string): IPromise<Array<FixtureRound>> {
		return this._$http.get(`${this._apiDomain}/round/options?Sport=${sportCode.toUpperCase()}`).then((response) => {
			if (response.data && (response.data as Array<FixtureRound>).length > 0) {
				const roundOptions = (response.data as Array<FixtureRound>).sort((a,b) => {
					if (a.Year !== b.Year) {
						return a.Year - b.Year;
					}

					return a.Round_Number - b.Round_Number;
				});

				return roundOptions;
				
			} else {
				throw "Round Options data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getRoundMatches(sportCode: string, roundNo: number, season: number, strip = false, bestBets = false, bookmakerList?: Array<string>): IPromise<Array<Match>> {
		return this._$http.get(`${this._apiDomain}/round/matches` + 
			`?Sport=${sportCode.toUpperCase()}` + 
			`&Round=${roundNo}` + 
			`&Season=${season}${strip ? "&strip=true" : ""}${bestBets ? `&best_bets=true&bookmakers=${bookmakerList.join(",")}` : ""}`).then((response) => {
			if (response.data) {
				return response.data as Array<Match>;
			} else {
				throw "Round Matches data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getTournamentMatches(tournamentID: string, strip = false, bestBets = false, bookmakerList?: Array<string>): IPromise<Array<Match>> {
		return this._$http.get(`${this._apiDomain}/tournament/${tournamentID}?${strip ? "strip=true&" : ""}${bestBets ? `best_bets=true&bookmakers=${bookmakerList.join(",")}` : ""}&`).then((response) => {
			if (response.data) {
				return response.data as Array<Match>;
			} else {
				throw "Tournament Matches data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getGolfTournamentData(tournamentID: string, season: string): IPromise<any> {
		return new Promise((resolve) => {
			if (tournamentID === "GOLF_AUTO") {
				this._$http.get(`${this._apiDomain}/tournaments`).then((response) => {
					if (Array.isArray(response.data)) {
						response.data.forEach(t => {
							t.startDateConverted = parseJSON(t.startDate);
							t.endDateConverted = parseJSON(t.endDate);
						});

						if (response.data.some(t => isFuture(addDays(t.endDateConverted, 1)) && t.futuresDataAvailable)) {
							const nextTournament = response.data.filter(t => isFuture(addDays(t.endDateConverted, 1)) && t.futuresDataAvailable)
								.sort((a,b) => compareAsc(a.startDateConverted, b.startDateConverted))[0];
							tournamentID = nextTournament.tournamentId;
							season = nextTournament.year;
						} else {
							let latestTournament;
							if (response.data.some(t => t.futuresDataAvailable)) {
								latestTournament = response.data.filter(t => t.futuresDataAvailable)
									.sort((a,b) => compareDesc(a.startDateConverted, b.startDateConverted))[0];
							} else {
								latestTournament = response.data.sort((a,b) => compareDesc(a.startDateConverted, b.startDateConverted))[0];
							}
							
							tournamentID = latestTournament.tournamentId;
							season = latestTournament.year;
						}
						
						resolve(null);
						
					} else {
						throw "Golf Tournament List data in unexpected format: " + response;
					}
					
				});
			} else {
				resolve(null);
			}
		})
			.then(() => {
				return this._$http.get(`${this._apiDomain}/tournament/${tournamentID}/${season}?futures=true`)
					.then((response) => {
						if (response.data) {
							return response.data;
							
						} else {
							throw "Golf Tournament data in unexpected format: " + response;
						}
						
					});
			})
			.catch((err) => {
				console.error(err);
				if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
					// this._Rollbar.warning("Levy call failed", {
					// 	error: err,
					// });
				}
				throw "API_CALL_FAILED";
			});
		

	}

	getTournamentOptions(): IPromise<Array<TennisTournament>> {
		return this._$http.get(`${this._apiDomain}/tournaments/tennis`).then((response) => {
			if (response.data) {
				const tournamentOptions = (response.data as Array<TennisTournament>).sort((a,b) => {
					if (a.Year !== b.Year) {
						return a.Year - b.Year;
					}

					return compareDesc(parseJSON(a.MaxDate), parseJSON(b.MaxDate));
				});

				return tournamentOptions;
				
			} else {
				throw "Tournament Options data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getNBASimulatorTeams(): IPromise<Array<Record<string, any>>> {
		return this._$http.get("https://gazza.statsinsider.com.au/nbasim-players.json").then((response) => {
			if (response.data && Array.isArray(response.data) && response.data.length > 0) {
				return response.data as Array<Record<string, any>>;
			} else {
				throw "Simulator Teams data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getNBASimulatorMatchupMatrix(): IPromise<Array<Record<string, any>>> {
		return this._$http.get("https://gazza.statsinsider.com.au/nbasim-matches.json").then((response) => {
			if (response.data && Array.isArray(response.data) && response.data.length > 0) {
				return response.data as Array<Record<string, any>>;
			} else {
				throw "Simulator Matchup Matrix data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Levy call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getResponsiveBanner(id: number): IPromise<Record<string, any>> {
		return this._$http.get<Record<string, any>>(`https://api-lb.dimers.com/api/v1/responsive-banners/${id}`).then((response) => {
			if (response.data && response.data.data) {
				return response.data.data;
			} else {
				throw "Responsive Banner data in unexpected format: " + response;
			}
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Dimers API call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getBestPromos(count: number, state: string, sportCode: string): IPromise<Array<BookOffer>> {
		return this._$http.get<Record<string, any>>(`https://api-lb.dimers.com/api/v1/bookmakers/best-promo?limit=${count}&state=${state}${
			sportCode ? `&sport_league=${sportCode.toUpperCase()}` : ""
		}`).then((response) => {
			if (response && response.data && response.data.data) {
				return response.data.data as Array<BookOffer>;
			} else {
				throw "Best Promos data in unexpected format: " + response;
			}
			
			
		}).catch((err) => {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Dimers API call failed", {
				// 	sport: sportCode,
				// 	state: state,
				// 	error: err,
				// });
			}
			
			throw "API_CALL_FAILED";
		});
	}

	getBestPromosFromCosmic(count: number, clientID: string):IPromise<Record<string, any>> {
		const query = {
			"type": "sportsbook-promos",
			"$and": [
				{
					"metadata.atad_clients": clientID,
				},
				{
					"metadata.placement_locations": "AL Sidebar"
				}
			]
		};

		const params = new URLSearchParams({
			"read_key": "kIszTMwQkliPYn5yOQCHtMAZoW76qEs5Kp8Kr2jxoXf2oIkMch",
			"query": JSON.stringify(query),
			"props": "slug,title,metadata",
			"depth": "1",
			"limit": `${count}`,
		});
    

		return this._$http.get<Record<string, any>>(`https://cosmic-proxy.statsinsider.com.au/api/v3/buckets/ciphercms-production/objects?${params}`).then((response) => {
			if (response && response.data && response.data.objects) {
				return response.data.objects;
			} else {
				throw "Best Promos data in unexpected format: " + response;
			}
		}).catch((err)=> {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Dimers API call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

	getPromoFromCosmic(promoID: string): IPromise<Record<string, any>> {

		const params = new URLSearchParams({
			"read_key": "kIszTMwQkliPYn5yOQCHtMAZoW76qEs5Kp8Kr2jxoXf2oIkMch",
			"props": "slug,title,metadata",
			"depth": "2",
		});
    

		return this._$http.get<Record<string, any>>(`https://cosmic-proxy.statsinsider.com.au/api/v3/buckets/ciphercms-production/objects/${promoID}?${params}`).then((response) => {
			if (response?.data?.object) {
				return response.data.object;
			} else {
				throw "Promo data in unexpected format: " + response;
			}
		}).catch((err)=> {
			console.error(err);
			if (!err.xhrStatus || (err.xhrStatus !== "abort" && err.xhrStatus !== "error")) {
				// this._Rollbar.warning("Dimers API call failed", {
				// 	error: err,
				// });
			}
			throw "API_CALL_FAILED";
		});
	}

}